// @ts-check
// pwa/lib/theme.js — runtime theme helper (light / dark / system).
//
// The critical-path bootstrap (pwa/theme-init.js) runs in <head> before the
// stylesheet and applies the resolved theme to <html>. This module owns
// EVERYTHING ELSE:
//   - getTheme() / setTheme() — programmatic access
//   - cycleTheme()            — light → dark → system → light
//   - watchSystem()           — re-resolve on OS-level prefers-color-scheme change
//   - subscribe()             — view layer can re-render the toggle label
//
// Three states; matches theme-init.js exactly:
//   'light'  — force light regardless of OS
//   'dark'   — force dark regardless of OS
//   'system' — follow prefers-color-scheme (default)

const STORAGE_KEY = 'hub-theme';
const VALID = new Set(['light', 'dark', 'system']);
const listeners = new Set();
let watchSystemAttached = false;
let mediaQuery = null;

function readStored() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return VALID.has(v) ? v : 'light';
  } catch {
    return 'light';
  }
}

function writeStored(t) {
  try {
    // 'light' is the default; null in storage means light, so we clear rather than write.
    if (t === 'light') localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, t);
  } catch {
    /* private mode — accept that the choice doesn't persist across sessions */
  }
}

/** Return the user's preference: 'light' | 'dark' | 'system'. */
export function getTheme() {
  return readStored();
}

/** Return the *resolved* theme actually in effect: 'light' | 'dark'. */
export function getResolvedTheme() {
  const pref = readStored();
  if (pref === 'light' || pref === 'dark') return pref;
  return prefersDark() ? 'dark' : 'light';
}

function prefersDark() {
  if (!mediaQuery) mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  return mediaQuery.matches;
}

function applyTheme(pref) {
  const isDark = pref === 'dark' || (pref === 'system' && prefersDark());
  document.documentElement.classList.toggle('dark', isDark);
}

/** Set the user's preference and apply it. Triggers subscribers. */
export function setTheme(t) {
  if (!VALID.has(t)) return;
  writeStored(t);
  applyTheme(t);
  for (const fn of listeners) fn(t);
}

/** Cycle: light → dark → system → light. */
export function cycleTheme() {
  const next = { light: 'dark', dark: 'system', system: 'light' }[getTheme()] || 'light';
  setTheme(next);
  return next;
}

/** Subscribe to preference changes. Returns an unsubscribe function. */
export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * Watch the OS-level prefers-color-scheme media query and re-resolve when it
 * changes — only relevant when the user's preference is 'system'. Idempotent.
 */
export function watchSystem() {
  if (watchSystemAttached) return;
  if (!mediaQuery) mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  mediaQuery.addEventListener('change', () => {
    if (getTheme() === 'system') applyTheme('system');
  });
  watchSystemAttached = true;
}
